import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X, KeyRound } from 'lucide-react';
import { can } from '../permissions';
import toast from 'react-hot-toast';
import api from '../api';

const empty = { name: '', phone: '', email: '', license_no: '', vehicle_no: '', type: 'delivery' };

const DRIVER_TYPE_LABELS = {
  delivery: 'Delivery Driver',
  presalesman: 'Presalesman',
  van_salesman: 'Van Salesman',
};

const DRIVER_TYPE_COLORS = {
  delivery: { bg: '#dbeafe', color: '#1d4ed8' },
  presalesman: { bg: '#fef3c7', color: '#b45309' },
  van_salesman: { bg: '#dcfce7', color: '#16a34a' },
};

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get('/drivers').then(r => setDrivers(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put('/drivers/' + editing, form); toast.success('Driver updated'); }
      else { await api.post('/drivers', form); toast.success('Driver added'); }
      setModal(false); setForm(empty); setEditing(null); load();
    } catch { toast.error('Error saving driver'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this driver?')) return;
    await api.delete('/drivers/' + id);
    toast.success('Driver removed'); load();
  };

  const edit = (d) => { setForm({ ...empty, ...d }); setEditing(d.id); setModal(true); };

  const [pwModal, setPwModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const setPassword = async () => {
    if (!newPassword || newPassword.length < 4) return toast.error('Password must be at least 4 characters');
    try {
      await api.post(`/drivers/${pwModal.id}/set-password`, { password: newPassword });
      toast.success(`Password set for ${pwModal.name}`);
      setPwModal(null);
      setNewPassword('');
    } catch { toast.error('Error setting password'); }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>Drivers</h1>
          <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>Manage your delivery team</p>
        </div>
        {can('drivers','edit') && <button onClick={() => { setForm(empty); setEditing(null); setModal(true); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: '#0284c7', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          <Plus size={16} /> Add driver
        </button>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        {drivers.map(d => {
          const typeStyle = DRIVER_TYPE_COLORS[d.type] || DRIVER_TYPE_COLORS.delivery;
          return (
          <div key={d.id} style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#1d4ed8', fontSize: 14 }}>
                {d.name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {can('drivers','edit') && <button onClick={() => setPwModal(d)} style={{ padding: 6, background: '#eff6ff', border: 'none', borderRadius: 6, cursor: 'pointer' }} title="Set password"><KeyRound size={14} color="#0284c7" /></button>}
                {can('drivers','edit') && <button onClick={() => edit(d)} title="Edit driver" style={{ padding: 6, background: '#f1f5f9', border: 'none', borderRadius: 6, cursor: 'pointer' }}><Pencil size={14} color="#475569" /></button>}
                {can('drivers','delete') && <button onClick={() => del(d.id)} title="Delete driver" style={{ padding: 6, background: '#fef2f2', border: 'none', borderRadius: 6, cursor: 'pointer' }}><Trash2 size={14} color="#dc2626" /></button>}
              </div>
            </div>
            <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 15, marginBottom: 4 }}>{d.name}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>{d.phone}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>{d.email}</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>Vehicle: {d.vehicle_no || '—'}</div>
            <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {d.type && (
                <span style={{
                  padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600,
                  background: typeStyle.bg, color: typeStyle.color
                }}>
                  {DRIVER_TYPE_LABELS[d.type] || d.type}
                </span>
              )}
              <span style={{
                padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600,
                background: d.password ? '#dcfce7' : '#fef9c3', color: d.password ? '#16a34a' : '#854d0e'
              }}>
                {d.password ? '🔒 Password set' : '⚠️ No password'}
              </span>
            </div>
          </div>
          );
        })}
      </div>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', margin: 0 }}>{editing ? 'Edit' : 'Add'} driver</h2>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color="#64748b" /></button>
            </div>
            <form onSubmit={submit}>
              {[['name','Full name'],['phone','Phone number'],['email','Email'],['license_no','License no.'],['vehicle_no','Vehicle no.']].map(([k,l]) => (
                <div key={k} style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 5 }}>{l}</label>
                  <input value={form[k] || ''} onChange={e => setForm({...form, [k]: e.target.value})}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              ))}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 5 }}>Driver type</label>
                <select value={form.type || 'delivery'} onChange={e => setForm({...form, type: e.target.value})}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', background: '#fff' }}>
                  <option value="delivery">Delivery Driver</option>
                  <option value="presalesman">Presalesman</option>
                  <option value="van_salesman">Van Salesman</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button type="button" onClick={() => setModal(false)}
                  style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                <button type="submit"
                  style={{ flex: 1, padding: 10, background: '#0284c7', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pwModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b', marginBottom: 4 }}>🔒 Set Password</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>{pwModal.name} — {pwModal.phone}</div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>New password</label>
            <input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              placeholder="Min 4 characters"
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, marginBottom: 20, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setPwModal(null); setNewPassword(''); }} style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={setPassword} style={{ flex: 1, padding: 10, background: '#0284c7', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Save Password</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
